// deps
import { useCallback, useMemo } from "react";

// internal deps
import { None, Option, Some } from "@swiggy-private/js-fp";
import { format } from "@swiggy-private/core-ui";

// helpers
import {
    generatePaymentMethodId,
    getPaymentMethods,
    isPaymentGroupEnabled,
    isPaymentMethodSelected,
} from "@payment-ui/helpers/Payment";

// interfaces
import {
    IPreferredPaymentGroup,
    PaymentMethod,
    PaymentMethodMeta,
    PreferredGroupActions,
} from "@payment-ui/interfaces";
import { PreferredRendererActions } from "@payment-ui/components/payment-group/preferred/interfaces";

// constants
import {
    PREFERRED_DEFAULT_FOLD_SIZE,
    PREFERRED_MORE_METHODS_CTA_TEXT,
    PREFERRED_ONE_MORE_METHOD_CTA_TEXT,
} from "@payment-ui/constants/Preferred";

type ReturnType = PreferredRendererActions & {
    paymentMethods: PaymentMethod<PaymentMethodMeta>[];
    canShowPreferredMoreCta: boolean;
    preferredMoreCtaText: Option<string>;
    foldedMethodsCount: number;
};

export function usePreferredPaymentGroup(props: IPreferredPaymentGroup): ReturnType {
    const { paymentActions, getPreferredSpecificActions, paymentData, showPreferredMoreCta } = props;

    const isGroupEnabled = useMemo((): boolean => isPaymentGroupEnabled(paymentData), [paymentData]);
    const totalPaymentMethods = useMemo((): PaymentMethod<PaymentMethodMeta>[] => getPaymentMethods(props), [props]);

    const [foldSize, foldedMethodsCount] = useMemo(() => {
        const { multiPpmFoldSize = PREFERRED_DEFAULT_FOLD_SIZE } = paymentData.meta ?? {};
        const foldSize = multiPpmFoldSize >= 0 ? multiPpmFoldSize : PREFERRED_DEFAULT_FOLD_SIZE;
        const foldedMethodsCount = totalPaymentMethods.length - foldSize;

        return [foldSize, foldedMethodsCount];
    }, [paymentData.meta, totalPaymentMethods.length]);

    // determines whether to show more cta or not
    const canShowPreferredMoreCta = useMemo(() => showPreferredMoreCta && foldSize > 0 && foldedMethodsCount > 0, [
        foldSize,
        foldedMethodsCount,
        showPreferredMoreCta,
    ]);

    // cta text to be shown on preferred group
    const preferredMoreCtaText = useMemo(() => {
        if (!canShowPreferredMoreCta) {
            return None;
        }
        const ctaTextConstruct =
            foldedMethodsCount === 1 ? PREFERRED_ONE_MORE_METHOD_CTA_TEXT : PREFERRED_MORE_METHODS_CTA_TEXT;

        return Some(format(ctaTextConstruct, String(foldedMethodsCount)));
    }, [canShowPreferredMoreCta, foldedMethodsCount]);

    const paymentMethods = useMemo(
        (): PaymentMethod<PaymentMethodMeta>[] =>
            canShowPreferredMoreCta ? totalPaymentMethods.slice(0, foldSize) : totalPaymentMethods,
        [foldSize, canShowPreferredMoreCta, totalPaymentMethods],
    );

    const getPaymentActions = useCallback(
        (groupName: string): PreferredGroupActions => {
            const otherActions = getPreferredSpecificActions(groupName);

            return { ...paymentActions, ...otherActions };
        },
        [getPreferredSpecificActions, paymentActions],
    );

    const getPaymentMethodProps = useCallback(
        (paymentMethod: PaymentMethod<PaymentMethodMeta>) => {
            const id = generatePaymentMethodId(paymentMethod);

            return {
                id,
                key: paymentMethod.displayName,
                isPaymentGroupEnabled: isGroupEnabled,
                selected: isPaymentMethodSelected(props.selectedPaymentId, id),
            };
        },
        [isGroupEnabled, props.selectedPaymentId],
    );

    return {
        getPaymentActions,
        getPaymentMethodProps,
        preferredMoreCtaText,
        paymentMethods,
        canShowPreferredMoreCta,
        foldedMethodsCount,
    };
}
